<template>
  <div class="contact-fieldset">
    <b-alert
      v-if="warning"
      show
      :variant="warningVariant"
      class="fieldset-warning"
    >
      {{ warning }}
    </b-alert>

    <b-form-group label="First name" label-size="sm">
      <b-form-input
        v-model="contact.firstname"
        size="sm"
        :disabled="disabled"
      />
    </b-form-group>
    <b-form-group label="Last name" label-size="sm">
      <b-form-input v-model="contact.lastname" size="sm" :disabled="disabled" />
      <span v-if="errors.lastname" class="text-danger small">{{
        errors.lastname
      }}</span>
    </b-form-group>
    <b-form-group label="Email" label-size="sm">
      <b-form-input v-model="contact.email" size="sm" :disabled="disabled" />
      <span v-if="errors.email" class="text-danger small">{{
        errors.email
      }}</span>
    </b-form-group>
    <b-form-group label="Phone" label-size="sm">
      <b-form-input
        v-model="contact.phone"
        size="sm"
        :disabled="disabled"
        @blur="normalizePhoneField('phone')"
      />
      <span v-if="errors.phone" class="text-danger small">{{
        errors.phone
      }}</span>
    </b-form-group>
    <b-form-group label="Mobile" label-size="sm">
      <b-form-input
        v-model="contact.mobilephone"
        size="sm"
        :disabled="disabled"
        @blur="normalizePhoneField('mobilephone')"
      />
      <span v-if="errors.mobile" class="text-danger small">{{
        errors.mobile
      }}</span>
    </b-form-group>
  </div>
</template>

<script>
// The contact fields, in one place - see BoatFieldset for why.
//
// Errors are shown but never block: these records predate the rules, and a
// dealer correcting a phone number should not be held up by a legacy email
// somebody else entered years ago. Salesforce will reject a genuinely
// malformed address, and the API repairs what it can before it gets there.
import { emailError, normalizePhone, phoneError } from "./contactValidation";

export default {
  name: "ContactFieldset",
  props: {
    contact: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    warning: { type: String, default: null },
    warningVariant: { type: String, default: "warning" },
  },
  computed: {
    errors() {
      return {
        lastname: this.contact.lastname ? null : "Last name is required",
        email: emailError(this.contact.email),
        phone: phoneError(this.contact.phone),
        mobile: phoneError(this.contact.mobilephone),
      };
    },
    // A per-record Save, so the dealer opened this contact deliberately and
    // the cost of insisting is a box, not a payment. The phone has to be
    // dialable before it is written - unlike the renewal form, where the same
    // rule would gate the charge.
    //
    // Email stays advisory: Salesforce repairs or drops a malformed address
    // via the API's sanitizer rather than refusing the record, so there is
    // nothing here that a save would break.
    blocking() {
      return Boolean(
        this.errors.lastname || this.errors.phone || this.errors.mobile
      );
    },
  },
  methods: {
    // Same repair the renewal form does: formatting a dealer typed is not a
    // mistake to report, so tidy it on the way out of the field. Only an
    // unambiguous ten digits is written back, so a half-typed or
    // letter-laden value stays put and its error keeps explaining itself.
    normalizePhoneField(field) {
      const current = this.contact[field];
      if (current === null || current === undefined || current === "") return;

      const digits = normalizePhone(current);
      if (digits.length === 10 && digits !== current && !phoneError(current)) {
        this.contact[field] = digits;
      }
    },
  },
  watch: {
    blocking: {
      immediate: true,
      handler(value) {
        this.$emit("validity", !value);
      },
    },
  },
};
</script>

<style scoped>
.fieldset-warning {
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
}
</style>
